import React, { useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useParams, useNavigate } from 'react-router-dom';
import useApp from './hooks/useApp';
import dataService from './services/dataService';
import LoadingScreen from './components/LoadingScreen';
import Sidebar from './components/Sidebar';
import HomePage from './components/HomePage';
import CategoryPage from './components/CategoryPage';
import HelpButton from './components/HelpButton';

function CategoryRouteWrapper() {
  const { categoryId } = useParams();
  const navigate = useNavigate();

  const {
    isSidebarOpen,
    categories,
    songs,
    categorySongCounts,
    loading,
    config,
    searchQuery,
    searchResults,
    currentCategory,
    currentPage,
    totalPages,
    paginationInfo,
    setIsSidebarOpen,
    setSearchQuery,
    setSearchResults,
    onSongClick,
    onPageChange,
  } = useApp(categoryId, navigate);

  const handleCategoryClick = (category) => {
    dataService.clearCache();
    if (category === 'home') {
      navigate('/');
    } else {
      navigate(`/category/${category}`);
    }
    setIsSidebarOpen(false);
    setSearchQuery('');
    setSearchResults([]);
  };

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <div className={`app-layout${isSidebarOpen ? ' sidebar-open' : ''}`}>
      {/* Sidebar rendered here, but hamburger moved inside CategoryPage */}
      <Sidebar
        categories={categories}
        activeCategory={categoryId}
        onCategoryClick={handleCategoryClick}
        isSidebarOpen={isSidebarOpen}
        setIsSidebarOpen={setIsSidebarOpen}
        categorySongCounts={categorySongCounts}
        config={config}
      />

      {/* Main content */}
      <div className="main-content">
        <CategoryPage
          currentCategory={currentCategory}
          songs={songs}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          searchResults={searchResults}
          config={config}
          onSongClick={onSongClick}
          currentPage={currentPage}
          totalPages={totalPages}
          paginationInfo={paginationInfo}
          onPageChange={onPageChange}
          categorySongCounts={categorySongCounts}
          onCategoryClick={handleCategoryClick}
          isSidebarOpen={isSidebarOpen}
          setIsSidebarOpen={setIsSidebarOpen}
        />
      </div>

      {/* Mobile overlay */}
      {isSidebarOpen && (
        <div className="mobile-overlay" onClick={() => setIsSidebarOpen(false)} />
      )}

      {/* Floating Help Button — always visible */}
      <HelpButton />
    </div>
  );
}


function HomeRouteWrapper() {
  const navigate = useNavigate();

  const {
    isSidebarOpen,
    categories,
    categorySongCounts,
    loading,
    config,
    searchQuery,
    searchResults,
    setIsSidebarOpen,
    setSearchQuery,
    setSearchResults,
    onSongClick,
  } = useApp(null, navigate);

  const handleCategoryClick = (category) => {
    dataService.clearCache();
    if (category === 'home') {
      navigate('/');
    } else {
      navigate(`/category/${category}`);
    }
    setIsSidebarOpen(false);
    setSearchQuery('');
    setSearchResults([]);
  };

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <div className="app-layout">
      {/* Main content — no sidebar/hamburger on homepage */}
      <div className="main-content">
        <HomePage
          config={config}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          searchResults={searchResults}
          onSongClick={onSongClick}
          categories={categories}
          onCategoryClick={handleCategoryClick}
          categorySongCounts={categorySongCounts}
        />
      </div>

      {/* Floating Help Button — always visible */}
      <HelpButton />
    </div>
  );
}


function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<HomeRouteWrapper />} />
        <Route path="/category/:categoryId" element={<CategoryRouteWrapper />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
